import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { CreateProduct } from '../ProductStore'
import { Alert } from 'reactstrap'
import {
  IBaseCategory,
  ComplianceCategoryIdMapping,
  IBaseProduct,
  ICompany,
} from '@getgreenline/homi-shared'
import { Routes } from '../../../../../constants/Routes'
import { Tag, Row } from 'antd'
import { GrRegex } from '../../../../../utilities/helpers'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}
interface Props extends InjectedProps {
  company: ICompany
  product: CreateProduct
  categories: IBaseCategory[]
  productsWithDuplicateSku: IBaseProduct[]
  errorMessage?: string
}

interface Error {
  errorMessage: string
  errorOrigin?: string
}
@inject('currentCompanyStore')
@observer
export class WarningSection extends React.Component<Props, {}> {
  makeWarnings = (product: CreateProduct): string[] => {
    const warnings: string[] = []

    // Warning if no category
    if (!product.categoryId) {
      warnings.push('No category attached')
    }
    // Warning if no vendor
    if (!product.supplierId) {
      warnings.push(`No brand attached`)
    }
    // Warning if no tax
    if (!product.taxGroupId) {
      warnings.push('No tax group attached')
    }
    // Warning if no tax
    if (!product.isActive) {
      warnings.push('This product is inactive and will not show up on your inventory and POS.')
    }

    return warnings
  }

  matchingSKUWarning = (productsWithDuplicateSku: IBaseProduct[]): string => {
    return productsWithDuplicateSku.length ? 'Matching SKUs found' : ''
  }

  checkErrorsForProductsWithVariants = (
    product: CreateProduct,
    matchingCategory: IBaseCategory,
    errors: Error[],
  ): Error[] => {
    // Variants should all have cannabis weights
    product.childProducts.forEach((variant, i) => {
      const variantName = variant.name ? variant.name : `Variant #${i + 1}`

      if (!variant.cannabisWeight) {
        errors.push({
          errorMessage: `${variantName} is missing a cannabis weight value`,
        })
      }

      if (
        this.props.company.province === 'SK' &&
        matchingCategory.complianceCategoryId !== ComplianceCategoryIdMapping.DRIED &&
        !variant.weight
      ) {
        errors.push({
          errorMessage: `${variantName} is missing a net product weight value`,
        })
      }
    })

    return errors
  }

  checkErrorsForProductWithoutVariants = (
    product: CreateProduct,
    matchingCategory: IBaseCategory,
    errors: Error[],
  ): Error[] => {
    const productName = product.name ? product.name : 'This product'

    // Top level product should have a cannabis weight
    if (!product.cannabisWeight) {
      errors.push({
        errorMessage: `${productName} is missing a cannabis weight value`,
      })
    }

    if (
      this.props.company.province === 'SK' &&
      matchingCategory.complianceCategoryId !== ComplianceCategoryIdMapping.DRIED &&
      !product.weight
    ) {
      errors.push({
        errorMessage: `${productName} is missing a net product weight value`,
      })
    }

    return errors
  }

  checkUnitError = (product: CreateProduct, errors: Error[]): Error[] => {
    const productName = product.name ? product.name : 'This product'
    if (product.unit !== 'unit') {
      errors.push({
        errorMessage: `${productName} must be tracked in units, not grams`,
        // errorOrigin: '#unit-of-measurement'
      })
    }

    return errors
  }

  makeErrors = (product: CreateProduct, categories: IBaseCategory[]): Error[] => {
    let errors: Error[] = []

    // Error if compliant category is attached but products don't have a cannabis weight attached
    // The editable product doesn't compute the compliance categories, so have to search here
    if (product.categoryId) {
      const matchingCategory = categories.find((c) => c.id === product.categoryId)

      if (matchingCategory && matchingCategory.complianceCategoryId) {
        errors = product.isMasterProduct
          ? this.checkErrorsForProductsWithVariants(product, matchingCategory, errors)
          : this.checkErrorsForProductWithoutVariants(product, matchingCategory, errors)

        // Compliant products must be in units, not grams
        errors = this.checkUnitError(product, errors)
      }
    }

    if (this.props.errorMessage) {
      errors.push({
        errorMessage: this.props.errorMessage,
      })
    }

    if (!product.name) {
      errors.push({
        errorMessage: 'Product name is missing',
        // errorOrigin: '#product-name-input'
      })
    }

    if (product.name.match(GrRegex.unicodeRegex) !== null) {
      errors.push({
        errorMessage: 'Product name contains prohibited characters',
      })
    }

    return errors
  }

  render() {
    const { company, product, categories, productsWithDuplicateSku } = this.props

    const warnings = this.makeWarnings(product)
    const matchingSKUwarning = this.matchingSKUWarning(productsWithDuplicateSku)
    const errors = this.makeErrors(product, categories)

    return (
      <>
        {errors.length > 0 && (
          <Alert id='product-error-wrapper' color='danger'>
            <b>Errors:</b>
            {errors.map((error, index) => {
              const { errorMessage, errorOrigin } = error
              const message = errorOrigin ? (
                <a href={`${errorOrigin}`}>{errorMessage}</a>
              ) : (
                errorMessage
              )
              return <li key={index}>{message}</li>
            })}
          </Alert>
        )}
        {matchingSKUwarning.length > 0 && (
          <Alert color='warning'>
            <b>Warning:</b>
            <li>{matchingSKUwarning}</li>
            <ul className='no-default-list-style similar-product-list'>
              {productsWithDuplicateSku.map((duplicateProduct) => (
                <div className='mb-1' key={duplicateProduct.id}>
                  <Row>
                    <a
                      target='_blank'
                      href={Routes.EDIT_PRODUCT(company.id, duplicateProduct.id)}
                      rel='noreferrer'
                    >
                      <b>{duplicateProduct.name}</b>
                    </a>

                    {duplicateProduct.isDeleted && <Tag>Deleted</Tag>}
                  </Row>
                  <div className='text-muted'>SKU: {duplicateProduct.sku || '-'}</div>
                </div>
              ))}
            </ul>
          </Alert>
        )}
        {warnings.length > 0 && (
          <Alert color='warning'>
            <b>Warning:</b>
            {warnings.map((w, index) => (
              <li key={index}>{w}</li>
            ))}
          </Alert>
        )}
      </>
    )
  }
}
